import React, { useEffect, useState } from 'react';
import { ENV, PADDLE_CLIENT_TOKEN, PUBLIC_URL } from '../utils/const';
import Layout from '../layouts/Layout';
import { PaddlePricing } from '../components/PaddlePricing';
import { Paddle, initializePaddle } from '@paddle/paddle-js';
import { useTranslation } from 'react-i18next';
import { Container, Grid, Button, Title, Text, Image, Accordion, Stack, Divider, Box, Group, Badge, Center } from '@mantine/core';
import { Head } from 'vite-react-ssg';
import { ICSLogoSVG, GoogleSVG, OutlookSVG, AppleSVG } from '../components/SVG';
import { useFathom } from '../contexts/FathomContext';
import { useLocation } from 'react-router-dom';
import MoreFromOurBlog from '../components/MoreFromOurBlog';
import { blogs } from '../_blog/routes';

const ForFreelancerInner: React.FC = () => {
    const { t, i18n } = useTranslation();
    const [paddle, setPaddle] = useState<Paddle | null>(null);
    const { trackEvent } = useFathom();
    const location = useLocation();

    const signup = (cta_name: string) => {
        trackEvent(`For Freelancers > ${cta_name} Clicked`);
        window.location.href = `${PUBLIC_URL}/dashboard`;
    }

    async function setupPaddle() {
        try {
            const paddleInstance = await initializePaddle({ environment: ENV === "production" ? "production" : "sandbox", token: PADDLE_CLIENT_TOKEN });
            if (paddleInstance) {
                setPaddle(paddleInstance);
            }
        } catch (e) {
            console.log("Couldn't initialize paddle")
        }
    }

    useEffect(() => {
        setupPaddle();
    }, [])

    const CTAButtonProps = { color: 'primary.4', radius: 'md', fw: 300, c: 'white', px: 'xl', size: "compact-xl" }

    return (
        <>
            <Head>
                <meta charSet="utf-8" />
                <title>{t('common.title_sync_calendars')}</title>
                <link rel="canonical" href={`https://calensync.live${location.pathname.replace('/en', '')}`} />
                <link rel="alternate" href={`https://calensync.live${PUBLIC_URL}/es/para-autónomos`} hrefLang="es" />
                <link rel="alternate" href={`https://calensync.live${PUBLIC_URL}/fr/for-freelancer`} hrefLang="fr" />
                <link rel="alternate" href={`https://calensync.live${PUBLIC_URL}/en/for-freelancer`} hrefLang="en" />
                <link rel="alternate" href={`https://calensync.live${PUBLIC_URL}/it/for-freelancer`} hrefLang="it" />
                <link rel="alternate" href={`https://calensync.live${PUBLIC_URL}/for-freelancer`} hrefLang="x-default" />
                <meta name="description" content={t("for_freelancers.meta.description")} />
                <meta property="og:title" content={t("for_freelancers.meta.og_title")} />
                <meta property="og:description" content={t("for_freelancers.meta.description")} />
            </Head>
            <Box className='hero'>
                <Container size="lg" py="xl">
                    <Grid align="center" justify="center" gap="xl" py={{ base: 'sm', md: 'xl' }}>
                        <Grid.Col span={{ base: 12, sm: 6 }}>
                            <Stack>
                                <Title order={1}>{t("for_freelancers.hero.title")}</Title>
                                <Title order={2} fw={200}>{t("for_freelancers.hero.heading")}</Title>
                                <Stack gap={'4'}>
                                    <Group grow={true} hiddenFrom="md">
                                        <Button {...CTAButtonProps} onClick={() => signup("hero")}>
                                            {t("for_freelancers.hero.cta")}
                                        </Button>
                                    </Group>
                                    <Group grow={false} visibleFrom="md">
                                        <Button {...CTAButtonProps} onClick={() => signup("hero")}>
                                            {t("for_freelancers.hero.cta")}
                                        </Button>
                                    </Group>
                                    <Text size="sm" >{t("common.free_trial")}</Text>
                                </Stack>
                            </Stack>
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, sm: 6 }}>
                            <Image
                                src={`${PUBLIC_URL}/hero.gif`}
                                alt="Synchronize your calendars GIF animation"
                                w="100%"
                            />
                        </Grid.Col>
                        <Grid.Col span={12} mt="xl">
                            <Text style={{ fontSize: 'calc(1.5rem + 1vw)' }} fw={300} ta='center' pb='lg'>Compatible with</Text>
                            <Grid>
                                <Grid.Col span={{ base: 6, sm: 3 }}>
                                    <Stack justify="center" align="center" gap='xs'>
                                        <OutlookSVG width={52} height={52} />
                                        <Group justify="center" align="center" gap='xs'>
                                            <Text size="xl" lh={1} ta='center'>Microsoft Outlook</Text>
                                        </Group>
                                    </Stack>
                                </Grid.Col>
                                <Grid.Col span={{ base: 6, sm: 3 }}>
                                    <Stack justify="center" align="center" gap='xs'>
                                        <GoogleSVG width={52} height={52} />
                                        <Text size="xl" lh={1} ta='center'>Google Calendar</Text>
                                    </Stack>
                                </Grid.Col>
                                <Grid.Col span={{ base: 6, sm: 3 }}>
                                    <Stack justify="center" align="center" gap='xs'>
                                        <AppleSVG width={52} height={52} />
                                        <Group justify="center" align="center" gap='xs'>
                                            <Text size="xl" lh={1} ta='center'>Apple Calendar</Text>
                                            <Badge color='primary.4'>New!</Badge>
                                        </Group>
                                    </Stack>
                                </Grid.Col>
                                <Grid.Col span={{ base: 6, sm: 3 }}>
                                    <Stack justify="center" align="center" gap='xs'>
                                        <ICSLogoSVG width={52} height={52} />
                                        <Group justify="center" align="center" gap='xs'>
                                            <Text size="xl" lh={1} ta='center'>iCalendar</Text>
                                        </Group>
                                    </Stack>
                                </Grid.Col>
                            </Grid>
                        </Grid.Col>
                    </Grid>
                </Container>
            </Box>

            <Box bg='gray.0' py="xl">
                <Container size="lg" id="features" my="xl">
                    <Grid gap={{ base: 'md', lg: 'xl' }}>
                        {[1, 2, 3, 4].map((num) => (
                            <Grid.Col key={num} span={{ base: 12, lg: 6 }} order={{ base: 1, lg: 2 }}>
                                <Box py="md">
                                    <Group gap="md" pb="md">
                                        <Title order={2} fw={500}>{t(`for_freelancers.value.${num}_head`)}</Title>
                                        {num >= 5 && <Badge>New!</Badge>}
                                    </Group>
                                    <Text size="xl" fw={200} lh={1.2}>{t(`for_freelancers.value.${num}_content`)}</Text>
                                </Box>
                            </Grid.Col>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Scheduling Section */}
            <Box bg='gray.0' py="xl">
                <Container size="lg" id="features" my="xl">
                    <Center my="xl">
                        <Title order={2} className='display-4'>{t("home.features.schedule_meetings")}</Title>
                    </Center>
                    <Grid gap={{ base: 'md', lg: 'xl' }} align="center">
                        <Grid.Col span={{ base: 12, lg: 5 }} order={{ base: 1, lg: 2 }}>
                            <Text size="xl" fw={200} lh={1.5} dangerouslySetInnerHTML={{ __html: t("home.features.schedule_meetings_content") }} />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, lg: 7 }} order={{ base: 1, lg: 2 }}>
                            <Box mx="auto" maw="600px">
                                <Image loading="lazy" src={`${PUBLIC_URL}/assets/scheduling.png`} alt="Calensync Event Scheduling Page" w="100%" />
                            </Box>
                        </Grid.Col>
                    </Grid>
                </Container>
            </Box>

            <Box className='hero' py="xl">
                {paddle &&
                    <PaddlePricing
                        paddle={paddle}
                        isHome={true}
                        size="lg"
                        onStartTrial={() => signup("pricing")}
                    />
                }
            </Box>
            <Container size="xl" py="xl">
                <Stack align="center" mb="xl">
                    <Title order={1}>F.A.Q.</Title>
                </Stack>
                <Accordion variant="contained" color='primary.4'>
                    {/* Convert each Accordion.Item to Mantine's Accordion.Item */}
                    {['00', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'].map((idx) => (
                        <div key={idx}>
                            <Accordion.Item value={idx}>
                                <Accordion.Control py='xs' styles={{ label: { fontSize: 'var(--mantine-font-size-lg)', fontWeight: '500' } }}>{t(`home.faq.q${idx}`)}</Accordion.Control>
                                <Accordion.Panel><Text fs='xl'>{t(`home.faq.a${idx}`)}</Text></Accordion.Panel>
                            </Accordion.Item>
                            <Divider color='gray.5' />
                        </div>
                    ))}
                </Accordion>

                <Box my={'10vh'} ta='center'>
                    <MoreFromOurBlog blogs={[blogs['efforless-meeting-scheduling'], blogs['maximize-remote-work-productivity-calendar-integration'], blogs['add-buffer-time-to-event-synchronization']]} matchingLanguageCode={i18n.language} />
                </Box>
                <Stack align="center" my={"10vh"}>
                    <Group grow={true} hiddenFrom="md">
                        <Button {...CTAButtonProps} size='xl' onClick={() => signup("hero")}>
                            {t("for_freelancers.hero.cta")}
                        </Button>
                    </Group>
                    <Group grow={false} visibleFrom="md">
                        <Button {...CTAButtonProps} size='xl' onClick={() => signup("hero")}>
                            {t("for_freelancers.hero.cta")}
                        </Button>
                    </Group>
                </Stack>
            </Container>
        </ >
    );
};


const ForFreelancer: React.FC = () => {
    return (
        <Layout>
            <ForFreelancerInner />
        </Layout>
    );
}

export default ForFreelancer;